import React, { useState, useEffect } from 'react';
import { Users, DollarSign, CalendarDays, Wifi, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../api';
import { colors, fontFamily, optionStyle, selectSurface } from '../theme';
import { buildAgentTree, nodeHasItems } from '../utils/agentGrouping';

const DIRECT_KEY = 'direct';

export default function PerformanceTab({ user, canViewIp = false }) {
  const [view, setView] = useState('weekly'); // weekly, customers, agents
  const [customerData, setCustomerData] = useState([]);
  const [agentData, setAgentData] = useState([]);
  const [weeklyData, setWeeklyData] = useState({ customers: [], days: [] });
  const [agentsForTree, setAgentsForTree] = useState([]);
  const [timePeriod, setTimePeriod] = useState('week'); // week, month, all
  const [weekOffset, setWeekOffset] = useState(0); // weekly view: 0 = this week, 1 = last week, ...
  const [sortBy, setSortBy] = useState('profit'); // profit, wagered, winnings, roi
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailedBets, setDetailedBets] = useState([]);
  const [collapsedAgents, setCollapsedAgents] = useState(new Set());

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, timePeriod, sortBy, sortOrder, weekOffset]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (view === 'customers') {
        const [custResp, agentResp] = await Promise.all([
          api.getCustomerPerformance(timePeriod, sortBy, sortOrder),
          api.getAgentPerformance(timePeriod, 'earnings', 'desc'),
        ]);
        setCustomerData(custResp.customers || []);
        setAgentsForTree(agentResp.agents || []);
      } else if (view === 'weekly') {
        const [weekResp, agentResp] = await Promise.all([
          api.getWeeklyBreakdown(weekOffset),
          api.getAgentPerformance('week', 'earnings', 'desc'),
        ]);
        setWeeklyData(weekResp);
        setAgentsForTree(agentResp.agents || []);
      } else {
        const response = await api.getAgentPerformance(timePeriod, sortBy, sortOrder);
        setAgentData(response.agents || []);
      }
    } catch (err) {
      console.error('Error loading performance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerDetail = async (customerId) => {
    try {
      const response = await api.getCustomerDetail(customerId, timePeriod);
      setDetailedBets(response.bets || []);
      setSelectedCustomer(response.customer);
    } catch (err) {
      console.error('Error loading customer detail:', err);
    }
  };

  const toggleAgentCollapsed = (key) => setCollapsedAgents(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // A customer matches if their own name/username matches OR their agent's name
  // matches — so typing an agent's name surfaces that agent's whole book, and
  // narrowing to a customer name filters down to that person.
  const q = filterText.trim().toLowerCase();
  const customerMatches = (item) => !q ||
    item.username.toLowerCase().includes(q) ||
    item.name.toLowerCase().includes(q) ||
    String(item.agent_name || '').toLowerCase().includes(q);
  const filteredCustomers = customerData.filter(customerMatches);
  const filteredAgents = agentData.filter(item => !q ||
    item.username.toLowerCase().includes(q) ||
    item.name.toLowerCase().includes(q) ||
    String(item.parent_name || '').toLowerCase().includes(q)
  );
  const filteredWeekly = (weeklyData.customers || []).filter(customerMatches);

  const cardStyle = { background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: '0.5rem' };
  const activeBtn = { background: colors.cardBgActive, color: colors.textPrimary, fontWeight: 'bold', fontFamily };
  const inactiveBtn = { background: colors.sectionBg, color: colors.textPrimary, fontFamily };
  const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: '0.375rem' };
  const mutedText = { color: colors.textMuted };
  const goldText = { color: colors.textPrimary };

  // Recursively walks an agent tree (roots + a "direct" bucket of items with
  // no agent) and flattens it into an ordered array of React elements —
  // one header element per agent group plus one item element per item,
  // skipping any group a text filter has emptied out entirely. Shared by
  // both the mobile-card and desktop-table renderings of the Customers and
  // Weekly Breakdown views, so the grouping logic exists exactly once.
  const flattenGroups = (roots, direct, renderHeader, renderItem) => {
    const out = [];
    const walk = (node, depth) => {
      if (!nodeHasItems(node)) return;
      const collapsed = collapsedAgents.has(node.agent.id);
      out.push(renderHeader(node.agent, depth, collapsed, () => toggleAgentCollapsed(node.agent.id)));
      if (!collapsed) {
        node.items.forEach(item => out.push(renderItem(item, depth + 1)));
        node.subAgents.forEach(sub => walk(sub, depth + 1));
      }
    };
    if (direct.length > 0) {
      const collapsed = collapsedAgents.has(DIRECT_KEY);
      out.push(renderHeader({ id: DIRECT_KEY, name: 'Direct (Book)' }, 0, collapsed, () => toggleAgentCollapsed(DIRECT_KEY)));
      if (!collapsed) direct.forEach(item => out.push(renderItem(item, 1)));
    }
    roots.forEach(node => walk(node, 0));
    return out;
  };

  const agentHeaderRow = (colSpan) => (agent, depth, collapsed, onToggle) => (
    <tr key={`h-${agent.id}`} onClick={onToggle} style={{ cursor: 'pointer', background: colors.sectionBg, borderTop: '1px solid rgba(191,149,63,0.2)' }}>
      <td colSpan={colSpan} style={{ padding: '0.5rem 1rem', paddingLeft: `${1 + depth * 1.25}rem` }}>
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight size={16} color={colors.gold} /> : <ChevronDown size={16} color={colors.gold} />}
          <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{agent.name}</span>
        </div>
      </td>
    </tr>
  );

  const agentHeaderCard = (agent, depth, collapsed, onToggle) => (
    <div
      key={`h-${agent.id}`}
      onClick={onToggle}
      className="rounded-lg px-3 py-2 flex items-center gap-2"
      style={{ ...cardStyle, marginLeft: depth * 0.75 + 'rem', cursor: 'pointer' }}
    >
      {collapsed ? <ChevronRight size={16} color={colors.gold} /> : <ChevronDown size={16} color={colors.gold} />}
      <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>{agent.name}</span>
    </div>
  );

  const winRateWithRecord = (customer) => `${(customer.win_rate ?? 0).toFixed(1)}% (${customer.won_bets}W-${customer.lost_bets}L)`;

  if (loading) {
    return <div className="text-center py-8 text-sm md:text-base" style={goldText}>Loading performance data...</div>;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header Controls */}
      <div className="p-3 md:p-6" style={cardStyle}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setView('weekly')}
              className="flex-1 md:flex-none px-3 md:px-4 py-2 rounded text-sm md:text-base"
              style={view === 'weekly' ? activeBtn : inactiveBtn}
            >
              <CalendarDays className="inline mr-1 md:mr-2" size={14} />
              Weekly Breakdown
            </button>
            <button
              onClick={() => setView('customers')}
              className="flex-1 md:flex-none px-3 md:px-4 py-2 rounded text-sm md:text-base"
              style={view === 'customers' ? activeBtn : inactiveBtn}
            >
              <Users className="inline mr-1 md:mr-2" size={14} />
              Customers
            </button>
            <button
              onClick={() => setView('agents')}
              className="flex-1 md:flex-none px-3 md:px-4 py-2 rounded text-sm md:text-base"
              style={view === 'agents' ? activeBtn : inactiveBtn}
            >
              <DollarSign className="inline mr-1 md:mr-2" size={14} />
              Agents
            </button>
          </div>

          <div className="flex gap-2">
            {view === 'weekly' && (
              <select
                value={weekOffset}
                onChange={(e) => setWeekOffset(Number(e.target.value))}
                className="rounded px-2 md:px-3 py-2 text-sm md:text-base flex-1 md:flex-none"
                style={{ ...inputStyle, background: selectSurface }}
              >
                <option value={0} style={optionStyle}>This Week</option>
                <option value={1} style={optionStyle}>Last Week</option>
                <option value={2} style={optionStyle}>2 Weeks Ago</option>
                <option value={3} style={optionStyle}>3 Weeks Ago</option>
                <option value={4} style={optionStyle}>4 Weeks Ago</option>
              </select>
            )}
            {view !== 'weekly' && (
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                className="rounded px-2 md:px-3 py-2 text-sm md:text-base flex-1 md:flex-none"
                style={{ ...inputStyle, background: selectSurface }}
              >
                <option value="week" style={optionStyle}>This Week</option>
                <option value="month" style={optionStyle}>This Month</option>
                <option value="all" style={optionStyle}>All Time</option>
              </select>
            )}

          </div>
        </div>

        <div className="mt-3 md:mt-4">
          <input
            type="text"
            placeholder="Filter by username or name..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full rounded px-3 py-2 text-sm md:text-base"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Customer Performance */}
      {view === 'customers' && (() => {
        const { roots, direct } = buildAgentTree(agentsForTree, filteredCustomers, c => c.agent_id);
        const anyCustomers = roots.some(nodeHasItems) || direct.length > 0;

        const customerCard = (customer, depth) => (
          <div key={customer.id} className="rounded-lg p-3" style={{ ...cardStyle, marginLeft: depth * 0.75 + 'rem' }}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate" style={goldText}>{customer.name}</div>
                <div className="text-xs font-mono" style={mutedText}>@{customer.username}</div>
                {customer.password_plain && (
                  <div className="text-[10px] font-mono" style={mutedText}>pw: {customer.password_plain}</div>
                )}
                {(user.role === 'book' || canViewIp) && customer.ip_addresses && customer.ip_addresses.length > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Wifi size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-500 font-mono">
                      {customer.ip_addresses.slice(0, 2).join(', ')}
                      {customer.ip_addresses.length > 2 && ` +${customer.ip_addresses.length - 2}`}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${customer.is_active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                  {customer.is_active ? 'Active' : 'Inactive'}
                </span>
                <button
                  onClick={() => loadCustomerDetail(customer.id)}
                  className="text-xs font-medium"
                  style={{color: colors.gold}}
                >
                  Details
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center pt-2 mt-2" style={{borderTop:'1px solid rgba(191,149,63,0.2)'}}>
              <div>
                <div className="text-[10px]" style={goldText}>Wagered</div>
                <div className="text-xs font-medium" style={goldText}>${(customer.total_wagered ?? 0).toFixed(0)}</div>
              </div>
              <div>
                <div className="text-[10px]" style={goldText}>Profit</div>
                <div className={`text-xs font-bold ${(customer.profit ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(customer.profit ?? 0) >= 0 ? '+' : ''}${(customer.profit ?? 0).toFixed(0)}
                </div>
              </div>
              <div>
                <div className="text-[10px]" style={goldText}>Win Rate</div>
                <div className="text-xs" style={goldText}>{winRateWithRecord(customer)}</div>
              </div>
              <div>
                <div className="text-[10px]" style={goldText}>Pending</div>
                <div className="text-xs font-medium" style={goldText}>{customer.pending_bets}</div>
              </div>
            </div>
          </div>
        );

        const customerRow = (customer) => (
          <tr key={customer.id} style={{borderTop:'1px solid rgba(191,149,63,0.15)'}}>
            <td className="px-4 py-3" style={{ paddingLeft: '1.5rem' }}>
              <div>
                <div className="font-medium" style={goldText}>{customer.name}</div>
                <div className="text-sm font-mono" style={mutedText}>@{customer.username}</div>
                {customer.password_plain && (
                  <div className="text-xs font-mono" style={mutedText}>pw: {customer.password_plain}</div>
                )}
              </div>
            </td>
            <td className="px-4 py-3 text-right font-medium">${(customer.total_wagered ?? 0).toFixed(2)}</td>
            <td className={`px-4 py-3 text-right font-bold ${(customer.profit ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {(customer.profit ?? 0) >= 0 ? '+' : ''}${(customer.profit ?? 0).toFixed(2)}
            </td>
            <td className="px-4 py-3 text-right">{winRateWithRecord(customer)}</td>
            <td className="px-4 py-3 text-center">{customer.pending_bets}</td>
            <td className="px-4 py-3 text-center">
              <span className={`px-2 py-1 rounded text-xs ${customer.is_active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                {customer.is_active ? 'Active' : 'Inactive'}
              </span>
            </td>
            {(user.role === 'book' || canViewIp) && (
              <td className="px-4 py-3 text-center text-xs font-mono" style={mutedText}>
                {customer.ip_addresses && customer.ip_addresses.length > 0 ? (
                  <div className="flex flex-col items-center gap-0.5">
                    {customer.ip_addresses.slice(0, 3).map((ip, i) => (
                      <span key={i}>{ip}</span>
                    ))}
                    {customer.ip_addresses.length > 3 && (
                      <span>+{customer.ip_addresses.length - 3} more</span>
                    )}
                  </div>
                ) : '-'}
              </td>
            )}
            <td className="px-4 py-3 text-center">
              <button
                onClick={() => loadCustomerDetail(customer.id)}
                className="text-sm font-medium"
                style={{color: colors.gold}}
              >
                Details
              </button>
            </td>
          </tr>
        );

        const colSpan = (user.role === 'book' || canViewIp) ? 8 : 7;

        return (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-2">
              {flattenGroups(roots, direct, agentHeaderCard, customerCard)}
              {!anyCustomers && (
                <div className="text-center py-8 text-sm" style={mutedText}>
                  No customers found matching filter
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto" style={cardStyle}>
              <table className="w-full" style={goldText}>
                <thead>
                  <tr style={{background:'rgba(0,0,0,0.3)',borderBottom:'1px solid rgba(191,149,63,0.2)'}}>
                    <th className="px-4 py-3 text-left cursor-pointer" style={goldText} onClick={() => setSortBy('username')}>
                      Username / Password
                    </th>
                    <th className="px-4 py-3 text-right cursor-pointer" style={goldText} onClick={() => setSortBy('wagered')}>
                      Wagered
                    </th>
                    <th className="px-4 py-3 text-right cursor-pointer" style={goldText} onClick={() => setSortBy('profit')}>
                      Profit
                    </th>
                    <th className="px-4 py-3 text-right cursor-pointer" style={goldText} onClick={() => setSortBy('win_rate')}>
                      Win Rate (W-L)
                    </th>
                    <th className="px-4 py-3 text-center" style={goldText}>Pending</th>
                    <th className="px-4 py-3 text-center" style={goldText}>Status</th>
                    {(user.role === 'book' || canViewIp) && <th className="px-4 py-3 text-center" style={goldText}>IPs</th>}
                    <th className="px-4 py-3 text-center" style={goldText}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {flattenGroups(roots, direct, agentHeaderRow(colSpan), customerRow)}
                </tbody>
              </table>

              {!anyCustomers && (
                <div className="text-center py-8" style={mutedText}>
                  No customers found matching filter
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Weekly Breakdown */}
      {view === 'weekly' && (() => {
        const { roots, direct } = buildAgentTree(agentsForTree, filteredWeekly, c => c.agent_id);
        const anyCustomers = roots.some(nodeHasItems) || direct.length > 0;
        const days = weeklyData.days || [];
        const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });

        const dayCell = (d) => (
          <span className={(d.profit ?? 0) > 0 ? 'text-green-400' : (d.profit ?? 0) < 0 ? 'text-red-400' : ''} style={(d.profit ?? 0) === 0 ? goldText : {}}>
            {(d.profit ?? 0) > 0 ? '+' : ''}${(d.profit ?? 0).toFixed(0)}
          </span>
        );

        const weeklyCard = (customer, depth) => (
          <div key={customer.id} className="rounded-lg p-3" style={{ ...cardStyle, marginLeft: depth * 0.75 + 'rem' }}>
            <div className="font-medium text-sm truncate" style={goldText}>{customer.name}</div>
            <div className="text-xs font-mono mb-2" style={mutedText}>@{customer.username}</div>
            <div className="grid grid-cols-7 gap-1 text-center mb-2">
              {customer.daily.map(d => (
                <div key={d.date}>
                  <div className="text-[9px]" style={goldText}>{dayLabel(d.date).split(' ')[0]}</div>
                  <div className="text-[10px] font-medium">{dayCell(d)}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-center pt-2" style={{borderTop:'1px solid rgba(191,149,63,0.2)'}}>
              <div>
                <div className="text-[10px]" style={goldText}>Weekly Balance</div>
                <div className={`text-xs font-bold ${(customer.weekly_balance ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(customer.weekly_balance ?? 0) >= 0 ? '+' : ''}${(customer.weekly_balance ?? 0).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[10px]" style={goldText}>Pending</div>
                <div className="text-xs font-medium" style={goldText}>${(customer.pending_amount ?? 0).toFixed(2)}</div>
              </div>
            </div>
          </div>
        );

        const weeklyRow = (customer) => (
          <tr key={customer.id} style={{borderTop:'1px solid rgba(191,149,63,0.15)'}}>
            <td className="px-4 py-3" style={{ paddingLeft: '1.5rem' }}>
              <div className="font-medium" style={goldText}>{customer.name}</div>
              <div className="text-sm font-mono" style={mutedText}>@{customer.username}</div>
            </td>
            {customer.daily.map(d => (
              <td key={d.date} className="px-2 py-3 text-center text-sm">
                {dayCell(d)}
              </td>
            ))}
            <td className={`px-4 py-3 text-right font-bold ${(customer.weekly_balance ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {(customer.weekly_balance ?? 0) >= 0 ? '+' : ''}${(customer.weekly_balance ?? 0).toFixed(2)}
            </td>
            <td className="px-4 py-3 text-right font-medium">${(customer.pending_amount ?? 0).toFixed(2)}</td>
          </tr>
        );

        const colSpan = 10;

        return (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-2">
              {flattenGroups(roots, direct, agentHeaderCard, weeklyCard)}
              {!anyCustomers && (
                <div className="text-center py-8 text-sm" style={mutedText}>
                  No customers found matching filter
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto" style={cardStyle}>
              <table className="w-full" style={goldText}>
                <thead>
                  <tr style={{background:'rgba(0,0,0,0.3)',borderBottom:'1px solid rgba(191,149,63,0.2)'}}>
                    <th className="px-4 py-3 text-left" style={goldText}>Customer</th>
                    {days.map(d => (
                      <th key={d} className="px-2 py-3 text-center" style={goldText}>{dayLabel(d)}</th>
                    ))}
                    <th className="px-4 py-3 text-right" style={goldText}>Weekly Balance</th>
                    <th className="px-4 py-3 text-right" style={goldText}>Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {flattenGroups(roots, direct, agentHeaderRow(colSpan), weeklyRow)}
                </tbody>
              </table>

              {!anyCustomers && (
                <div className="text-center py-8" style={mutedText}>
                  No customers found matching filter
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Agent Performance */}
      {view === 'agents' && (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredAgents.map((agent) => (
              <div key={agent.id} className="rounded-lg p-3" style={cardStyle}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate" style={goldText}>{agent.name}</div>
                    <div className="text-xs font-mono" style={mutedText}>@{agent.username}</div>
                    {agent.password_plain && (
                      <div className="text-[10px] font-mono" style={mutedText}>pw: {agent.password_plain}</div>
                    )}
                    <div className="text-[10px] mt-1" style={mutedText}>Parent: {agent.parent_name}</div>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${agent.is_active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                    {agent.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center pt-2 mt-2" style={{borderTop:'1px solid rgba(191,149,63,0.2)'}}>
                  <div>
                    <div className="text-[10px]" style={goldText}>Cut</div>
                    <div className="text-xs font-medium" style={goldText}>{(agent.agent_cut ?? 0).toFixed(0)}%</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={goldText}>Customers</div>
                    <div className="text-xs font-medium" style={goldText}>{agent.customer_count ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={goldText}>Earnings</div>
                    <div className="text-xs font-bold text-green-400">${(agent.earnings ?? 0).toFixed(0)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center pt-2 mt-2" style={{borderTop:'1px solid rgba(191,149,63,0.2)'}}>
                  <div>
                    <div className="text-[10px]" style={goldText}>Wagered</div>
                    <div className="text-xs font-medium" style={goldText}>${(agent.total_wagered ?? 0).toFixed(0)}</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={goldText}>Won</div>
                    <div className="text-xs font-medium" style={goldText}>${(agent.total_winnings ?? 0).toFixed(0)}</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={goldText}>Net Losses</div>
                    <div className={`text-xs font-medium ${(agent.net_losses ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>${(agent.net_losses ?? 0).toFixed(0)}</div>
                  </div>
                </div>
                <div className="text-center pt-2 mt-2" style={{borderTop:'1px solid rgba(191,149,63,0.2)'}}>
                  <div className="text-[10px]" style={goldText}>Per Customer</div>
                  <div className="text-xs font-medium" style={goldText}>${(agent.earnings_per_customer ?? 0).toFixed(2)}</div>
                </div>
              </div>
            ))}
            {filteredAgents.length === 0 && (
              <div className="text-center py-8 text-sm" style={mutedText}>
                No agents found matching filter
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto" style={cardStyle}>
            <table className="w-full" style={goldText}>
              <thead>
                <tr style={{background:'rgba(0,0,0,0.3)',borderBottom:'1px solid rgba(191,149,63,0.2)'}}>
                  <th className="px-4 py-3 text-left" style={goldText}>Agent</th>
                  <th className="px-4 py-3 text-left" style={goldText}>Parent</th>
                  <th className="px-4 py-3 text-right cursor-pointer" style={goldText} onClick={() => setSortBy('cut')}>
                    Cut %
                  </th>
                  <th className="px-4 py-3 text-right cursor-pointer" style={goldText} onClick={() => setSortBy('customers')}>
                    Customers
                  </th>
                  <th className="px-4 py-3 text-right" style={goldText}>Customer Wagered</th>
                  <th className="px-4 py-3 text-right" style={goldText}>Customer Won</th>
                  <th className="px-4 py-3 text-right" style={goldText}>Net Losses</th>
                  <th className="px-4 py-3 text-right cursor-pointer" style={goldText} onClick={() => setSortBy('earnings')}>
                    Earnings
                  </th>
                  <th className="px-4 py-3 text-right" style={goldText}>Per Customer</th>
                  <th className="px-4 py-3 text-center" style={goldText}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map((agent) => (
                  <tr key={agent.id} style={{ borderTop: '1px solid rgba(191,149,63,0.15)' }}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium" style={goldText}>{agent.name}</div>
                        <div className="text-sm font-mono" style={mutedText}>@{agent.username}</div>
                        {agent.password_plain && (
                          <div className="text-xs font-mono" style={mutedText}>pw: {agent.password_plain}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm" style={mutedText}>{agent.parent_name}</td>
                    <td className="px-4 py-3 text-right font-medium">{(agent.agent_cut ?? 0).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-right">{agent.customer_count ?? 0}</td>
                    <td className="px-4 py-3 text-right">${(agent.total_wagered ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">${(agent.total_winnings ?? 0).toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${(agent.net_losses ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${(agent.net_losses ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-green-400">
                      ${(agent.earnings ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm" style={mutedText}>
                      ${(agent.earnings_per_customer ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${agent.is_active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                        {agent.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredAgents.length === 0 && (
              <div className="text-center py-8" style={mutedText}>
                No agents found matching filter
              </div>
            )}
          </div>
        </>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="rounded-lg max-w-6xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto scrollbar-hide" style={{background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`}}>
            <div className="p-3 md:p-6 sticky top-0 z-10" style={{background: colors.bgGradient, borderBottom:'1px solid rgba(191,149,63,0.2)'}}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg md:text-2xl font-bold" style={goldText}>{selectedCustomer.name}</h2>
                  <p className="text-xs md:text-sm" style={mutedText}>Detailed Betting History</p>
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-2xl"
                  style={mutedText}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-2 md:p-6">
              {/* Mobile Card View for Bets */}
              <div className="md:hidden space-y-2">
                {detailedBets.map((bet) => (
                  <div key={`${bet.type === 'transaction' ? 'txn' : bet.is_parlay ? 'p' : 'b'}-${bet.id}`}
                       className="rounded-lg p-2"
                       style={{background: bet.type === 'transaction' ? 'rgba(217,119,6,0.15)' : 'rgba(0,0,0,0.3)', border: `1px solid ${bet.type === 'transaction' ? 'rgba(217,119,6,0.3)' : 'rgba(191,149,63,0.15)'}`}}>
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex-1 min-w-0">
                        <span className={`text-[10px] px-1 py-0.5 rounded ${
                          bet.type === 'transaction' ? 'bg-amber-900 text-amber-300' :
                          bet.is_parlay ? 'bg-purple-900 text-purple-300' : 'bg-blue-900 text-blue-300'
                        }`}>
                          {bet.type === 'transaction' ? bet.transaction_type :
                           bet.is_parlay ? 'Parlay' : (bet.market_category || 'Bet')}
                        </span>
                        {bet.type !== 'transaction' && (
                          <span className={`ml-1 text-[10px] px-1 py-0.5 rounded ${
                            bet.status === 'won' ? 'bg-green-900 text-green-300' :
                            bet.status === 'lost' ? 'bg-red-900 text-red-300' :
                            (bet.status === 'void' || bet.status === 'push') ? 'bg-gray-800 text-gray-300' :
                            'bg-yellow-900 text-yellow-300'
                          }`}>
                            {bet.status === 'won' ? 'W' : bet.status === 'lost' ? 'L' : bet.status === 'void' ? 'V' : bet.status === 'push' ? '=' : 'P'}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium" style={goldText}>${(bet.amount ?? 0).toFixed(0)}</div>
                        <div className={`text-xs font-bold ${
                          (bet.profit ?? 0) > 0 ? 'text-green-400' : (bet.profit ?? 0) < 0 ? 'text-red-400' : ''
                        }`} style={(bet.profit ?? 0) === 0 ? mutedText : {}}>
                          {bet.profit != null
                            ? (bet.profit > 0 ? `+$${bet.profit.toFixed(0)}` : bet.profit < 0 ? `-$${Math.abs(bet.profit).toFixed(0)}` : '$0')
                            : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs mt-1" style={goldText}>
                      {(() => {
                        if (bet.type !== 'transaction' && !bet.is_parlay && bet.player_name && bet.selection_type) {
                          const dir = bet.selection_type === 'over' ? 'Over' : bet.selection_type === 'under' ? 'Under' : bet.selection_type === 'yes' ? 'Yes' : bet.selection_type === 'no' ? 'No' : '';
                          const line = bet.line || bet.point;
                          return `${bet.description}${dir ? ` - ${dir}` : ''}${line ? ` (${line > 0 ? '+' : ''}${line})` : ''}`;
                        }
                        return bet.description || bet.selection || 'N/A';
                      })()}
                    </div>
                    {bet.is_parlay && bet.legs && (
                      <div className="mt-1 space-y-0.5 border-l-2 border-purple-300 pl-2">
                        {bet.legs.slice(0, 3).map((leg, i) => {
                          const isPlayerProp = leg.market_type && (leg.market_type.startsWith('player_') || leg.market_type.startsWith('batter_') || leg.market_type.startsWith('pitcher_'));
                          const dir = leg.selection_type === 'over' ? 'Over' : leg.selection_type === 'under' ? 'Under' : leg.selection_type === 'yes' ? 'Yes' : leg.selection_type === 'no' ? 'No' : '';
                          let display;
                          if (isPlayerProp && leg.player_name) {
                            display = `${leg.player_name}${dir ? ` - ${dir}` : ''}`;
                          } else if (leg.selection && !['Yes','No','Over','Under'].includes(leg.selection)) {
                            display = leg.selection;
                          } else if (leg.player_name) {
                            display = `${leg.player_name}${dir ? ` - ${dir}` : ''}`;
                          } else {
                            display = dir || leg.selection || leg.description || 'Bet';
                          }
                          if (leg.point) display += ` (${leg.point > 0 ? '+' : ''}${leg.point})`;
                          return (
                          <div key={i} className="text-[10px]" style={mutedText}>
                            {display}
                            {leg.status && leg.status !== 'pending' && (
                              <span className={`ml-1 ${leg.status === 'won' ? 'text-green-400' : 'text-red-400'}`}>
                                {leg.status === 'won' ? '✓' : '✗'}
                              </span>
                            )}
                          </div>
                          );
                        })}
                        {bet.legs.length > 3 && (
                          <div className="text-[10px]" style={mutedText}>+{bet.legs.length - 3} more legs</div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[10px] mt-1" style={mutedText}>
                      <span>{bet.created_at ? new Date(bet.created_at).toLocaleDateString() : '-'}</span>
                      {(user.role === 'book' || canViewIp) && bet.ip_address && bet.type !== 'transaction' && (
                        <span className="flex items-center gap-0.5 font-mono">
                          <Wifi size={8} />
                          {bet.ip_address.length > 12 ? bet.ip_address.substring(0, 12) + '...' : bet.ip_address}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full" style={goldText}>
                  <thead>
                    <tr style={{background:'rgba(0,0,0,0.3)',borderBottom:'1px solid rgba(191,149,63,0.2)'}}>
                      <th className="px-2 py-2 text-left text-xs w-20" style={mutedText}>Date</th>
                      <th className="px-3 py-2 text-left text-xs min-w-[400px]" style={mutedText}>Description</th>
                      <th className="px-2 py-2 text-left text-xs w-16" style={mutedText}>Type</th>
                      <th className="px-2 py-2 text-right text-xs w-16" style={mutedText}>Amt</th>
                      <th className="px-2 py-2 text-center text-xs w-14" style={mutedText}>Odds</th>
                      <th className="px-2 py-2 text-right text-xs w-16" style={mutedText}>Payout</th>
                      <th className="px-2 py-2 text-right text-xs w-16" style={mutedText}>Profit</th>
                      <th className="px-2 py-2 text-center text-xs w-20" style={mutedText}>Status</th>
                      <th className="px-2 py-2 text-center text-xs w-12" style={mutedText}>Settle</th>
                      {(user.role === 'book' || canViewIp) && <th className="px-2 py-2 text-center text-xs w-24" style={mutedText}>IP</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {detailedBets.map((bet) => (
                      <tr key={`${bet.type === 'transaction' ? 'txn' : bet.is_parlay ? 'p' : 'b'}-${bet.id}`}
                          style={{borderTop:'1px solid rgba(191,149,63,0.15)', background: bet.type === 'transaction' ? 'rgba(217,119,6,0.1)' : 'transparent'}}>
                        <td className="px-2 py-2 text-xs whitespace-nowrap">
                          <div style={goldText}>{bet.created_at ? new Date(bet.created_at).toLocaleDateString('en-US', {month: 'numeric', day: 'numeric'}) : '-'}</div>
                          <div style={mutedText}>{bet.created_at ? new Date(bet.created_at).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'}) : ''}</div>
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <div>
                            {(() => {
                              // For single player prop bets, include direction (Over/Under) and line
                              if (bet.type !== 'transaction' && !bet.is_parlay && bet.player_name && bet.selection_type) {
                                const direction = bet.selection_type === 'over' ? 'Over' : bet.selection_type === 'under' ? 'Under' : bet.selection_type === 'yes' ? 'Yes' : bet.selection_type === 'no' ? 'No' : '';
                                const line = bet.line || bet.point;
                                return `${bet.description}${direction ? ` - ${direction}` : ''}${line ? ` (${line > 0 ? '+' : ''}${line})` : ''}`;
                              }
                              return bet.description || bet.selection || 'N/A';
                            })()}
                            {bet.type !== 'transaction' && !bet.is_parlay && (bet.market_category || bet.bet_type) && (
                              <span className="text-purple-400 ml-1">
                                [{bet.market_category || bet.bet_type}]
                              </span>
                            )}
                          </div>
                          {bet.is_parlay && bet.legs && (
                            <div className="mt-2 space-y-1 pl-2" style={{borderLeft:'2px solid rgba(168,85,247,0.5)'}}>
                              {bet.legs.map((leg, i) => {
                                // Build a clear display for each parlay leg
                                const isPlayerProp = leg.market_type && (leg.market_type.startsWith('player_') || leg.market_type.startsWith('batter_') || leg.market_type.startsWith('pitcher_'));
                                const direction = leg.selection_type === 'over' ? 'Over' : leg.selection_type === 'under' ? 'Under' : leg.selection_type === 'yes' ? 'Yes' : leg.selection_type === 'no' ? 'No' : '';

                                // For player props, prefer showing: PlayerName - Direction
                                // For regular bets, show the team selection
                                let displaySelection;
                                if (isPlayerProp && leg.player_name) {
                                  displaySelection = `${leg.player_name}${direction ? ` - ${direction}` : ''}`;
                                } else if (leg.selection && leg.selection !== 'Yes' && leg.selection !== 'No' && leg.selection !== 'Over' && leg.selection !== 'Under') {
                                  displaySelection = leg.selection;
                                } else if (leg.player_name) {
                                  displaySelection = `${leg.player_name}${direction ? ` - ${direction}` : ''}`;
                                } else {
                                  displaySelection = `${direction || leg.selection || leg.description || 'Bet'}`;
                                }

                                return (
                                <div key={i} className="text-xs">
                                  {/* Show team matchup for context */}
                                  {(leg.away_team || leg.home_team) && (
                                    <span className="mr-1" style={mutedText}>
                                      {leg.away_team && leg.home_team
                                        ? `${leg.away_team} @ ${leg.home_team}:`
                                        : (leg.away_team || leg.home_team) + ':'}
                                    </span>
                                  )}
                                  <span className="font-medium" style={goldText}>
                                    {displaySelection}
                                  </span>
                                  <span className="text-purple-400 ml-1">
                                    [{leg.market_category || leg.bet_type || 'Bet'}]
                                  </span>
                                  {leg.point && (
                                    <span className="text-gray-500 ml-1">
                                      ({leg.point > 0 ? '+' : ''}{leg.point})
                                    </span>
                                  )}
                                  <span className="text-blue-600 ml-1">{leg.odds}</span>
                                  {leg.status && leg.status !== 'pending' && (
                                    <span className={`ml-1 ${leg.status === 'won' ? 'text-green-400' : leg.status === 'void' ? '' : 'text-red-400'}`} style={leg.status === 'void' ? mutedText : {}}>
                                      {leg.status === 'won' ? '✓' : leg.status === 'void' ? '⊘' : '✗'}
                                    </span>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded ${
                            bet.type === 'transaction' ? 'bg-amber-900 text-amber-300' :
                            bet.is_parlay ? 'bg-purple-900 text-purple-300' : 'bg-blue-900 text-blue-300'
                          }`}>
                            {bet.type === 'transaction' ? bet.transaction_type :
                             bet.is_parlay ? 'Parlay' : (bet.market_category || bet.bet_type || 'Bet')}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right text-xs font-medium">${(bet.amount ?? 0).toFixed(0)}</td>
                        <td className="px-2 py-2 text-center font-mono text-xs">{bet.odds || '-'}</td>
                        <td className="px-2 py-2 text-right text-xs font-medium">
                          {bet.payout != null && bet.payout > 0 ? (
                            `$${bet.payout.toFixed(0)}`
                          ) : bet.status === 'lost' ? (
                            '$0'
                          ) : bet.status === 'pending' && bet.potential_payout ? (
                            <span style={mutedText}>${bet.potential_payout.toFixed(0)}</span>
                          ) : bet.status === 'pending' && bet.odds ? (
                            <span style={mutedText}>
                              ${(() => {
                                const odds = bet.odds;
                                let decimal = 2.0;
                                if (typeof odds === 'string') {
                                  const numOdds = parseInt(odds.replace('+', ''));
                                  decimal = numOdds > 0 ? (numOdds / 100) + 1 : (100 / Math.abs(numOdds)) + 1;
                                }
                                return ((bet.amount || 0) * decimal).toFixed(0);
                              })()}
                            </span>
                          ) : '-'}
                        </td>
                        <td className={`px-2 py-2 text-right text-xs font-bold ${
                          (bet.profit ?? 0) > 0 ? 'text-green-400' : (bet.profit ?? 0) < 0 ? 'text-red-400' : ''
                        }`}>
                          {bet.profit != null
                            ? (bet.profit > 0 ? `+$${bet.profit.toFixed(0)}` : bet.profit < 0 ? `-$${Math.abs(bet.profit).toFixed(0)}` : '$0')
                            : '-'}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${
                            bet.status === 'won' ? 'bg-green-900 text-green-300' :
                            bet.status === 'lost' ? 'bg-red-900 text-red-300' :
                            (bet.status === 'void' || bet.status === 'push') ? 'bg-gray-800 text-gray-300' :
                            'bg-yellow-900 text-yellow-300'
                          }`}>
                            {bet.status === 'won' ? 'W' : bet.status === 'lost' ? 'L' : bet.status === 'void' ? 'V' : bet.status === 'push' ? '=' : 'P'}
                            {bet.is_parlay && bet.num_legs && (
                              <span className="ml-0.5">({bet.won_legs || 0}/{bet.num_legs})</span>
                            )}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center text-xs" style={mutedText}>
                          {bet.time_to_settle_hours ? `${bet.time_to_settle_hours.toFixed(0)}h` : '-'}
                        </td>
                        {(user.role === 'book' || canViewIp) && (
                          <td className="px-2 py-2 text-center text-xs font-mono" style={mutedText}>
                            {bet.ip_address && bet.type !== 'transaction' ? (
                              <span title={bet.ip_address}>
                                {bet.ip_address.length > 15 ? bet.ip_address.substring(0, 15) + '...' : bet.ip_address}
                              </span>
                            ) : '-'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
